const RELOAD_MARKER_KEY = 'credu:chunk-load-reload'

const CHUNK_LOAD_MESSAGE_RE =
	/Loading( CSS)? chunk [\d]+ failed|Failed to fetch dynamically imported module/i

let reloadScheduled = false

export const isChunkLoadError = (error: unknown): boolean => {
	if (!error || typeof error !== 'object') {
		return false
	}

	const name = 'name' in error ? String(error.name) : ''
	const message = 'message' in error ? String(error.message) : ''

	return name === 'ChunkLoadError' || CHUNK_LOAD_MESSAGE_RE.test(message)
}

const hasReloadMarker = (): boolean => {
	try {
		return sessionStorage.getItem(RELOAD_MARKER_KEY) === '1'
	} catch {
		return false
	}
}

const setReloadMarker = (): void => {
	try {
		sessionStorage.setItem(RELOAD_MARKER_KEY, '1')
	} catch {
		// Private mode can throw; reload without a marker is still better than a dead page.
	}
}

const clearReloadMarker = (): void => {
	try {
		sessionStorage.removeItem(RELOAD_MARKER_KEY)
	} catch {
		// ignore
	}
}

/**
 * Reloads once after a stale webpack chunk 404 (typical right after deploy).
 * Returns true when a reload was triggered so the UI can stay on a loader.
 */
export const reloadOnceOnChunkError = (error: unknown): boolean => {
	if (!isChunkLoadError(error)) {
		return false
	}

	if (reloadScheduled) {
		return true
	}

	if (hasReloadMarker()) {
		return false
	}

	reloadScheduled = true
	setReloadMarker()
	window.location.reload()
	return true
}

/** Wraps a React.lazy factory so a stale chunk reloads instead of rejecting. */
export const recoverFromChunkLoadError = <T>(
	loader: () => Promise<T>
): Promise<T> => {
	return loader()
		.then((result) => {
			clearReloadMarker()
			return result
		})
		.catch((error: unknown) => {
			if (reloadOnceOnChunkError(error)) {
				return new Promise<T>(() => undefined)
			}

			throw error
		})
}
